import companies from 'modules/core/store/collections/companies'
import legalEntities from 'modules/core/store/collections/legalEntities'

import type { SupplementType } from 'modules/payroll_bundle/components/common/helper/worksheet'
import type Supplement from 'modules/payroll_bundle/store/models/Supplement'

import type { SupplementAmount } from './types'

/**
 * Depending of the supplement type units, the supplement amount object would have different properties.
 *
 * When it is a money supplement value, then it is important to include the currency as we are showing totals per currency.
 *
 * It is important to understand that one suplement object can contain different amounts which represent different supplement types,
 * for example:
 *
 * {
      "last_name": "The Boss",
      "1674582067303": "2000",
      "1674582078887": "845",
      "first_name": "Bob",
      "userId": 21,
      "IBAN": "ES33 7644 9161 4190 1192 3454",
      "employee_id": 5,
      "id": 1,
      "full_name": "Bob The Boss",
      "legal_entity_id": 1,
      "legal_entity_name": "Everyday Software Legal Name"
 *  }
 *
 * As you can see in this supplement object example, it includes the properties 1674582067303 and 1674582078887 which represents the values
 * of the supplements types with that id. That's why this method returns a list of SupplementAmount.
 */
function getSupplementAmount(
  supplement: Supplement,
  supplementTypes: SupplementType[]
): SupplementAmount[] {
  const supplementAmounts: SupplementAmount[] = []

  for (const supplementType of supplementTypes) {
    const amount = supplement.getSupplementTypeAmount(supplementType.id)

    if (!amount) {
      continue
    }

    switch (supplementType.units) {
      case 'money':
        const supplementLegalEntity = legalEntities.get(supplement.legalEntityId)
        const currency = supplementLegalEntity
          ? supplementLegalEntity.currency
          : companies.current.attributes.currency

        supplementAmounts.push({ amount, currency, units: 'money' })
        break
      default:
        supplementAmounts.push({ amount, units: 'units' })
        break
    }
  }

  return supplementAmounts
}

/**
 * It returns a list of supplement amounts from a list of supplements.
 *
 * In order to create a supplement amount object, we need information from its suplement type, concretely the "units" property. Units property
 * (which could be "money" or "units") is used to calculate the totals of the supplements imported as every unit makes a different calculation.
 */
export default function getSupplementsAmounts(
  supplements: Supplement[],
  supplementTypes: SupplementType[]
): SupplementAmount[] {
  return supplements.flatMap((supplement) => getSupplementAmount(supplement, supplementTypes))
}
